import React from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import api from '../../api';
import useHttpData from '../../hooks/useHttpData';
import AsyncSection from './AsyncSection';
import { HEALTH_BUCKETS } from './gatewayHealth';

// `issue` is a health bucket key, so the dot takes that bucket's colour — the same one
// the gateway's history chart and its report pill use. Unknown keys fall back to grey.
const issueColor = issue => (HEALTH_BUCKETS[issue] ?? HEALTH_BUCKETS.offline).color;

// Compact "how long the issue has persisted" from the streak-start timestamp.
const durationSince = iso => {
    if (!iso) {
        return '—';
    }
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (Number.isNaN(mins) || mins < 1) {
        return '< 1 min';
    }
    if (mins < 60) {
        return `${mins} min`;
    }
    const hours = Math.round(mins / 60);
    if (hours < 24) {
        return `${hours} h`;
    }
    return `${Math.round(hours / 24)} d`;
};

// Fleet health page companion to the alarm breakdown: the gateways that are
// currently offline or reporting an issue, clickable through to the gateway page.
const GatewayNeedsAttention = () => {
    const history = useHistory();
    const { data, isReady, error } = useHttpData(() => api.get_gateways_needing_attention(), []);
    const gateways = data?.data ?? [];

    const open = gw => history.push({ pathname: `/gateways/${gw.device_id}`, state: { gateway: gw } });

    return (
        <div className="gateways-overview__alarms">
            <h2 className="gateways-overview__alarms-title">Needs attention</h2>
            <p className="gateways-overview__alarms-hint">Gateways currently offline or reporting issues.</p>
            <AsyncSection
                isReady={isReady}
                error={error}
                isEmpty={gateways.length === 0}
                errorText="Could not load gateways."
                emptyText="All gateways healthy."
            >
                <ul className="gw-attention__list">
                    {gateways.map(gw => (
                        <li key={gw.device_id} className="gw-attention__item" onClick={() => open(gw)}>
                            <span className="gw-attention__dot" style={{ backgroundColor: issueColor(gw.issue) }} />
                            <div className="gw-attention__main">
                                <span className="gw-attention__name">{gw.name}</span>
                                <span className="gw-attention__meta">
                                    {gw.retailer_customer_id} · {gw.reference}
                                </span>
                            </div>
                            <div className="gw-attention__issue">
                                <span className="gw-attention__issue-label">{gw.issue_label || HEALTH_BUCKETS[gw.issue]?.label || '—'}</span>
                                <span className="gw-attention__since">for {durationSince(gw.since)}</span>
                            </div>
                        </li>
                    ))}
                </ul>
            </AsyncSection>
        </div>
    );
};

export default GatewayNeedsAttention;
